import React, { useState, useRef, useEffect } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { friendlyError } from '../lib/permissions'
import { regLine } from '../lib/prescription'
import { CLINIC_HOURS } from '../config/clinicHours'

function fmt12(t) {
  const [h, m] = t.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  teal950: '#082828', teal900: '#0d3d3d', teal800: '#0f5454',
  teal700: '#126868', teal600: '#148080', teal500: '#19a8a8',
  teal400: '#2ecece', teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red600: '#dc2626',
  green50: '#f0fdf4', green600: '#16a34a',
  amber50: '#fffbeb', amber600: '#d97706',
  purple50: '#faf5ff', purple600: '#7c3aed',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

const APP_VERSION = '1.0.0'
const LS_KEY = 'md_settings'

// Clinic name/address/phone/email live in the `clinics` table and doctor credentials
// in `user_profiles`; only device-level preferences are kept in localStorage.
const DEFAULTS = {
  // GSTIN — leave blank until the clinic is GST-registered (no GSTIN line is printed while empty)
  clinicGST: '',
  // App preferences
  workingHoursStart: '09:00',
  workingHoursEnd: '20:00',
  workingDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  slotDuration: '30',
  currencySymbol: '₹',
  defaultGST: '18',
}

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ─── Helpers ──────────────────────────────────────────────────────────────────
function loadSettings() {
  try {
    const raw = localStorage.getItem(LS_KEY)
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS }
  } catch { return { ...DEFAULTS } }
}

function saveSettings(data) {
  localStorage.setItem(LS_KEY, JSON.stringify(data))
}

// ─── Sub-components ────────────────────────────────────────────────────────────
function SectionCard({ title, icon, children }) {
  return (
    <div style={{
      background: T.white, borderRadius: 14,
      boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
      overflow: 'hidden', marginBottom: 20,
    }}>
      <div style={{
        padding: '16px 24px', borderBottom: `1px solid ${T.gray100}`,
        display: 'flex', alignItems: 'center', gap: 10,
        background: T.gray50,
      }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>{title}</span>
      </div>
      <div style={{ padding: '20px 24px' }}>{children}</div>
    </div>
  )
}

function FieldRow({ label, children, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
      <div style={{ width: 200, flexShrink: 0, paddingTop: 9 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.gray700 }}>{label}</div>
        {hint && <div style={{ fontSize: 11.5, color: T.gray400, marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>{children}</div>
    </div>
  )
}

function TextInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
        border: `1.5px solid ${T.gray200}`, outline: 'none', fontFamily: T.font,
        boxSizing: 'border-box', color: T.gray900,
        transition: 'border-color 0.15s',
      }}
      onFocus={e => e.target.style.borderColor = T.teal500}
      onBlur={e => e.target.style.borderColor = T.gray200}
    />
  )
}

function SelectInput({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: '9px 12px', borderRadius: 8, fontSize: 13,
        border: `1.5px solid ${T.gray200}`, outline: 'none', fontFamily: T.font,
        color: T.gray900, background: T.white, cursor: 'pointer',
        minWidth: 120,
      }}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

// ─── Main Settings page ────────────────────────────────────────────────────────
export default function Settings() {
  const { clinic, doctors, isAdmin, refreshClinic } = useAuth()
  const [data, setData] = useState(loadSettings)
  const [clinicForm, setClinicForm] = useState({ name: '', tagline: '', address: '', phone: '', email: '' })
  const [clinicMsg, setClinicMsg]   = useState('')
  const [clinicSaving, setClinicSaving] = useState(false)
  useEffect(() => {
    if (clinic) setClinicForm({ name: clinic.name || '', tagline: clinic.tagline || '', address: clinic.address || '', phone: clinic.phone || '', email: clinic.email || '' })
  }, [clinic])
  function setC(k, v) { setClinicForm(f => ({ ...f, [k]: v })) }

  async function saveClinic() {
    if (!clinic?.id) return
    if (!clinicForm.name.trim()) { setClinicMsg('❌ Clinic name is required'); return }
    setClinicSaving(true); setClinicMsg('')
    const { error } = await supabase.from('clinics').update({
      name:    clinicForm.name.trim(),
      tagline: clinicForm.tagline.trim() || null,
      address: clinicForm.address.trim() || null,
      phone:   clinicForm.phone.trim() || null,
      email:   clinicForm.email.trim() || null,
    }).eq('id', clinic.id)
    setClinicSaving(false)
    if (error) { setClinicMsg('❌ ' + friendlyError(error)); return }
    await refreshClinic()
    setClinicMsg('✅ Clinic details saved')
    setTimeout(() => setClinicMsg(''), 2500)
  }
  const [saved, setSaved] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState('')
  const fileRef = useRef(null)


  function set(k, v) { setData(d => ({ ...d, [k]: v })) }

  function toggleDay(day) {
    setData(d => ({
      ...d,
      workingDays: d.workingDays.includes(day)
        ? d.workingDays.filter(x => x !== day)
        : [...d.workingDays, day],
    }))
  }

  function handleSave() {
    saveSettings(data)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  // ── Export all data ─────────────────────────────────────────────────────────
  async function handleExport() {
    setExporting(true)
    try {
      const tables = ['patients', 'appointments', 'invoices', 'prescriptions',
                      'inventory', 'staff_members', 'feedback', 'patient_photos']
      const snapshot = { exportedAt: new Date().toISOString(), version: APP_VERSION, tables: {} }

      await Promise.all(tables.map(async t => {
        const { data: rows } = await supabase.from(t).select('*')
        snapshot.tables[t] = rows || []
      }))

      const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `mysa-desk-backup-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      alert('Export failed: ' + e.message)
    } finally {
      setExporting(false)
    }
  }

  // ── Import from JSON ────────────────────────────────────────────────────────
  async function handleImport(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const confirmed = window.confirm('This will overwrite existing data. Are you sure?')
    if (!confirmed) {
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    setImporting(true); setImportMsg('')
    try {
      const text = await file.text()
      const snapshot = JSON.parse(text)
      if (!snapshot.tables) throw new Error('Invalid backup file')

      let totalRows = 0
      const ORDER = ['patients', 'appointments', 'invoices', 'prescriptions',
                     'inventory', 'staff_members', 'feedback', 'patient_photos']
      for (const t of ORDER) {
        const rows = snapshot.tables[t]
        if (!rows || !rows.length) continue
        const { error } = await supabase.from(t).upsert(rows, { onConflict: 'id' })
        if (error) throw new Error(`${t}: ${error.message}`)
        totalRows += rows.length
      }
      setImportMsg(`✅ Imported ${totalRows} records from backup`)
    } catch (err) {
      setImportMsg('❌ ' + err.message)
    } finally {
      setImporting(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', fontFamily: T.font, background: T.gray50 }}>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 32px' }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: T.gray900, margin: 0 }}>⚙️ Settings</h1>
            <p style={{ fontSize: 13, color: T.gray400, margin: '4px 0 0' }}>
              Manage clinic information and app preferences
            </p>
          </div>
          <button onClick={handleSave} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: 10, border: 'none',
            background: saved
              ? `linear-gradient(135deg, ${T.green600} 0%, #22c55e 100%)`
              : `linear-gradient(135deg, ${T.teal700} 0%, ${T.teal500} 100%)`,
            color: T.white, fontSize: 13.5, fontWeight: 700,
            cursor: 'pointer', fontFamily: T.font,
            boxShadow: `0 4px 14px ${saved ? T.green600 : T.teal700}44`,
            transition: 'background 0.3s',
          }}>
            {saved ? '✅ Saved!' : '💾 Save Changes'}
          </button>
        </div>

        {/* ── 1. Clinic Information (clinics table) ── */}
        <SectionCard title="Clinic Information" icon="🏥">
          {!clinic ? (
            <div style={{ fontSize: 13, color: T.gray400 }}>Clinic details are not available for this login.</div>
          ) : isAdmin ? (
            <>
              <FieldRow label="Clinic Name" hint="Shown on invoices, prescriptions & messages">
                <TextInput value={clinicForm.name} onChange={v => setC('name', v)} placeholder="Clinic name" />
              </FieldRow>
              <FieldRow label="Tagline" hint="Shown below the clinic name (optional)">
                <TextInput value={clinicForm.tagline} onChange={v => setC('tagline', v)} placeholder="Tagline" />
              </FieldRow>
              <FieldRow label="Address" hint="Full clinic address">
                <TextInput value={clinicForm.address} onChange={v => setC('address', v)} placeholder="Street, City, State, PIN" />
              </FieldRow>
              <FieldRow label="Phone Number" hint="Used on printouts and in reminder messages">
                <TextInput value={clinicForm.phone} onChange={v => setC('phone', v)} placeholder="Clinic phone" type="tel" />
              </FieldRow>
              <FieldRow label="Email">
                <TextInput value={clinicForm.email} onChange={v => setC('email', v)} placeholder="Clinic email" type="email" />
              </FieldRow>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                {clinicMsg && <span style={{ fontSize: 12.5, fontWeight: 600, color: clinicMsg.startsWith('✅') ? T.green600 : T.red600 }}>{clinicMsg}</span>}
                <button onClick={saveClinic} disabled={clinicSaving} style={{
                  padding: '8px 18px', borderRadius: 8, border: 'none', background: T.teal600, color: T.white,
                  fontSize: 13, fontWeight: 700, cursor: clinicSaving ? 'not-allowed' : 'pointer', fontFamily: T.font,
                }}>{clinicSaving ? 'Saving…' : '💾 Save Clinic Details'}</button>
              </div>
            </>
          ) : (
            [['Clinic Name', clinic.name], ['Tagline', clinic.tagline], ['Address', clinic.address], ['Phone', clinic.phone], ['Email', clinic.email]].map(([l, v]) => (
              <FieldRow key={l} label={l}><div style={{ paddingTop: 9, fontSize: 13, color: T.gray700 }}>{v || '—'}</div></FieldRow>
            ))
          )}
          <FieldRow label="GSTIN" hint="Leave blank until GST-registered — no GSTIN line is printed while empty">
            <TextInput value={data.clinicGST} onChange={v => set('clinicGST', v)} placeholder="e.g. 27ABCDE1234F1Z5" />
          </FieldRow>
        </SectionCard>

        {/* ── 2. Doctors (user_profiles) ── */}
        <SectionCard title="Doctors" icon="👨‍⚕️">
          <div style={{ fontSize: 12.5, color: T.gray500, marginBottom: 12 }}>
            Registered doctors from user profiles. These details appear on prescriptions and in the doctor lists.
          </div>
          {doctors.length === 0 ? (
            <div style={{ fontSize: 13, color: T.gray400 }}>No doctor with a registration number is set up for this clinic.</div>
          ) : doctors.map(d => (
            <div key={d.id} style={{ padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.gray200}`, background: T.gray50, marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>{d.full_name}</div>
              {d.qualification && <div style={{ fontSize: 12.5, color: T.teal700, fontWeight: 600, marginTop: 2 }}>{d.qualification}</div>}
              <div style={{ fontSize: 12, color: T.gray500, marginTop: 2 }}>{regLine(d)}</div>
            </div>
          ))}
        </SectionCard>

        {/* ── 3. App Preferences ── */}
        <SectionCard title="App Preferences" icon="🎛️">
          {/* Working hours */}
          <FieldRow label="Working Hours">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 12, color: T.gray500 }}>From</label>
                <input
                  type="time" value={data.workingHoursStart}
                  onChange={e => set('workingHoursStart', e.target.value)}
                  style={{
                    padding: '8px 10px', borderRadius: 8, fontSize: 13,
                    border: `1.5px solid ${T.gray200}`, outline: 'none', fontFamily: T.font,
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 12, color: T.gray500 }}>To</label>
                <input
                  type="time" value={data.workingHoursEnd}
                  onChange={e => set('workingHoursEnd', e.target.value)}
                  style={{
                    padding: '8px 10px', borderRadius: 8, fontSize: 13,
                    border: `1.5px solid ${T.gray200}`, outline: 'none', fontFamily: T.font,
                  }}
                />
              </div>
            </div>
          </FieldRow>

          {/* Working days */}
          <FieldRow label="Working Days">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {WEEK_DAYS.map(day => {
                const active = data.workingDays.includes(day)
                return (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    style={{
                      padding: '6px 14px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                      cursor: 'pointer', fontFamily: T.font, transition: 'all 0.15s',
                      border: `1.5px solid ${active ? T.teal600 : T.gray200}`,
                      background: active ? T.teal50 : T.white,
                      color: active ? T.teal700 : T.gray500,
                    }}
                  >{day}</button>
                )
              })}
            </div>
          </FieldRow>

          {/* Slot duration */}
          <FieldRow label="Appointment Slot" hint="Minutes per appointment slot">
            <SelectInput
              value={data.slotDuration}
              onChange={v => set('slotDuration', v)}
              options={[
                { value: '15', label: '15 minutes' },
                { value: '20', label: '20 minutes' },
                { value: '30', label: '30 minutes' },
                { value: '45', label: '45 minutes' },
                { value: '60', label: '60 minutes' },
              ]}
            />
          </FieldRow>

          {/* Currency */}
          <FieldRow label="Currency Symbol" hint="Shown on invoices and reports">
            <SelectInput
              value={data.currencySymbol}
              onChange={v => set('currencySymbol', v)}
              options={[
                { value: '₹', label: '₹ Indian Rupee' },
                { value: '$', label: '$ US Dollar' },
                { value: '€', label: '€ Euro' },
                { value: '£', label: '£ British Pound' },
              ]}
            />
          </FieldRow>

          {/* Default GST */}
          <FieldRow label="Default GST Rate" hint="Applied on new invoices">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number" min="0" max="100" value={data.defaultGST}
                onChange={e => set('defaultGST', e.target.value)}
                style={{
                  width: 80, padding: '9px 12px', borderRadius: 8, fontSize: 13,
                  border: `1.5px solid ${T.gray200}`, outline: 'none', fontFamily: T.font,
                }}
                onFocus={e => e.target.style.borderColor = T.teal500}
                onBlur={e => e.target.style.borderColor = T.gray200}
              />
              <span style={{ fontSize: 14, color: T.gray500 }}>%</span>
            </div>
          </FieldRow>
        </SectionCard>

        {/* ── 4. Clinic Schedule (read-only — src/config/clinicHours.js) ── */}
        <SectionCard title="Clinic Schedule" icon="🕐">
          <FieldRow label="Clinic Hours" hint="Usual hours — shown as a light label; booking outside them is allowed">
            <div style={{ paddingTop: 9, fontSize: 13, color: T.gray700 }}>{fmt12(CLINIC_HOURS.open)} – {fmt12(CLINIC_HOURS.close)}</div>
          </FieldRow>
          <FieldRow label="Lunch Break" hint="Shown as a label; still bookable">
            <div style={{ paddingTop: 9, fontSize: 13, color: T.gray700 }}>
              {CLINIC_HOURS.lunchStart && CLINIC_HOURS.lunchEnd ? `${fmt12(CLINIC_HOURS.lunchStart)} – ${fmt12(CLINIC_HOURS.lunchEnd)}` : 'None'}
            </div>
          </FieldRow>
          <div style={{ fontSize: 11.5, color: T.gray400 }}>These times are set in the app configuration (clinicHours.js).</div>
        </SectionCard>

        {/* ── 5. Data Management ── */}
        <SectionCard title="Data Management" icon="🗄️">

          {/* Export & Import — admin only */}
          {isAdmin ? (
            <>
              {/* Export */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 18px', borderRadius: 10, border: `1.5px solid ${T.gray200}`,
                background: T.gray50, marginBottom: 14, flexWrap: 'wrap', gap: 12,
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>Export All Data</div>
                  <div style={{ fontSize: 12.5, color: T.gray500, marginTop: 3 }}>
                    Download a full JSON backup of patients, appointments, invoices, and all clinic data
                  </div>
                </div>
                <button onClick={handleExport} disabled={exporting} style={{
                  padding: '9px 20px', borderRadius: 8,
                  border: `1.5px solid ${T.teal600}`, background: T.teal50,
                  color: T.teal700, fontSize: 13, fontWeight: 700,
                  cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.7 : 1,
                  fontFamily: T.font, flexShrink: 0,
                }}>
                  {exporting ? '⏳ Exporting…' : '⬇️ Export JSON'}
                </button>
              </div>

              {/* Import */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 18px', borderRadius: 10, border: `1.5px solid ${T.gray200}`,
                background: T.gray50, marginBottom: 14, flexWrap: 'wrap', gap: 12,
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>Import from Backup</div>
                  <div style={{ fontSize: 12.5, color: T.gray500, marginTop: 3 }}>
                    Restore data from a previously exported JSON file. Existing records will be updated, new ones added.
                  </div>
                  {importMsg && (
                    <div style={{
                      marginTop: 8, fontSize: 12.5, fontWeight: 600,
                      color: importMsg.startsWith('✅') ? T.green600 : T.red600,
                    }}>{importMsg}</div>
                  )}
                </div>
                <button onClick={() => fileRef.current?.click()} disabled={importing} style={{
                  padding: '9px 20px', borderRadius: 8,
                  border: `1.5px solid ${T.amber600}`, background: T.amber50,
                  color: T.amber600, fontSize: 13, fontWeight: 700,
                  cursor: importing ? 'not-allowed' : 'pointer', opacity: importing ? 0.7 : 1,
                  fontFamily: T.font, flexShrink: 0,
                }}>
                  {importing ? '⏳ Importing…' : '⬆️ Import JSON'}
                </button>
                <input
                  ref={fileRef} type="file" accept=".json"
                  onChange={handleImport}
                  style={{ display: 'none' }}
                />
              </div>
            </>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '16px 18px', borderRadius: 10, marginBottom: 14,
              border: `1.5px solid ${T.gray200}`, background: T.gray50,
            }}>
              <span style={{ fontSize: 20 }}>🔒</span>
              <span style={{ fontSize: 13, color: T.gray500 }}>
                Only admins can access backup and restore features
              </span>
            </div>
          )}

          {/* App version info */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', borderRadius: 10, border: `1.5px solid ${T.gray100}`,
            background: T.white, flexWrap: 'wrap', gap: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, background: T.teal600, borderRadius: 9,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
              }}>🌿</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>Mysa Desk</div>
                {clinic?.name && <div style={{ fontSize: 11.5, color: T.gray400 }}>Built for {clinic.name}</div>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                display: 'inline-block', padding: '4px 12px', borderRadius: 12,
                background: T.teal50, color: T.teal700,
                fontSize: 12, fontWeight: 700, border: `1px solid ${T.teal100}`,
              }}>v{APP_VERSION}</div>
              <div style={{ fontSize: 11, color: T.gray400, marginTop: 4 }}>
                {new Date().getFullYear()} · All rights reserved
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Bottom save button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8, paddingBottom: 32 }}>
          <button onClick={handleSave} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 32px', borderRadius: 10, border: 'none',
            background: saved
              ? `linear-gradient(135deg, ${T.green600} 0%, #22c55e 100%)`
              : `linear-gradient(135deg, ${T.teal700} 0%, ${T.teal500} 100%)`,
            color: T.white, fontSize: 14, fontWeight: 700,
            cursor: 'pointer', fontFamily: T.font,
            boxShadow: `0 4px 18px ${saved ? T.green600 : T.teal700}55`,
            transition: 'background 0.3s',
          }}>
            {saved ? '✅ All Changes Saved!' : '💾 Save All Changes'}
          </button>
        </div>

      </div>
    </div>
  )
}
